import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FlagRibbon from "@/components/FlagRibbon";
import EscudoBadge from "@/components/EscudoBadge";

export default function Footer() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "pe";
  const prefix = `/${lang}`;

  const nav = [
    { to: `${prefix}`, label: t("nav.home"), testid: "footer-nav-home" },
    { to: `${prefix}/privacy`, label: t("nav.privacy"), testid: "footer-nav-privacy" },
    { to: `${prefix}/cookies`, label: t("nav.cookies"), testid: "footer-nav-cookies" },
    { to: `${prefix}/terms`, label: t("nav.terms"), testid: "footer-nav-terms" },
  ];

  const legalRows = [
    { label: t("footer.address_label"), value: t("footer.address_value") },
    { label: t("footer.ruc_label"), value: t("footer.ruc_value") },
    { label: t("footer.registry_label"), value: t("footer.registry_value") },
    { label: t("footer.license_label"), value: t("footer.license_value") },
    { label: t("footer.jurisdiction_label"), value: t("footer.jurisdiction_value") },
    { label: t("footer.incorporated_label"), value: t("footer.incorporated_value") },
  ];

  return (
    <footer
      className="mt-24 text-slate-200"
      style={{ backgroundColor: "#0B1D3A" }}
      data-testid="site-footer"
    >
      <FlagRibbon />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-white p-1.5">
              <EscudoBadge className="h-9 w-9" />
            </span>
            <span
              className="text-white text-lg"
              style={{ fontFamily: "Merriweather, serif", fontWeight: 700 }}
            >
              {t("footer.company")}
            </span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-slate-300">
            {t("footer.description")}
          </p>
        </div>

        <div>
          <h3
            className="text-sm uppercase tracking-widest text-emerald-300"
            style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
          >
            {t("footer.legal_title")}
          </h3>
          <dl className="mt-4 space-y-2 text-sm">
            {legalRows.map((r) => (
              <div key={r.label}>
                <dt className="text-slate-400 text-xs uppercase tracking-wider">
                  {r.label}
                </dt>
                <dd className="text-slate-100">{r.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div>
          <h3 className="text-sm uppercase tracking-widest text-emerald-300">
            {t("footer.nav_title")}
          </h3>
          <ul className="mt-4 space-y-2 text-sm">
            {nav.map((n) => (
              <li key={n.to}>
                <Link
                  to={n.to}
                  data-testid={n.testid}
                  className="text-slate-200 hover:text-white underline-offset-4 hover:underline"
                >
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h3 className="text-sm uppercase tracking-widest text-emerald-300">
            {t("footer.contact_title")}
          </h3>
          <ul className="mt-4 space-y-2 text-sm">
            <li>
              <span className="block text-slate-400 text-xs uppercase tracking-wider">
                {t("footer.email_label")}
              </span>
              <a
                href={`mailto:${t("footer.email_value")}`}
                className="text-slate-100 hover:text-white"
                data-testid="footer-email-link"
              >
                {t("footer.email_value")}
              </a>
            </li>
            <li>
              <span className="block text-slate-400 text-xs uppercase tracking-wider">
                {t("footer.phone_label")}
              </span>
              <a
                href={`tel:${t("footer.phone_value").replace(/\s+/g, "")}`}
                className="text-slate-100 hover:text-white"
                data-testid="footer-phone-link"
              >
                {t("footer.phone_value")}
              </a>
            </li>
            <li>
              <span className="block text-slate-400 text-xs uppercase tracking-wider">
                {t("footer.hours_label")}
              </span>
              <span className="text-slate-100">{t("footer.hours_value")}</span>
            </li>
          </ul>
        </div>
      </div>

      <div
        className="border-t border-white/10"
        style={{ backgroundColor: "#08152B" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-400 text-center">
          {t("footer.rights")}
        </div>
      </div>
    </footer>
  );
}
